import type { SituationSource } from "@peer-start/platform-contracts";
import { Button } from "@peer-start/ui";
import {
  Alert,
  AlertDescription,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@peer-start/ui/reducate";
import { CircleAlert } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SituationSourceSummary } from "./SituationSourceSummary.js";

export function UnappliedSourcesNotice({
  sources,
  pending,
  onAcknowledge,
}: {
  sources: readonly SituationSource[];
  pending: boolean;
  onAcknowledge: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Alert className="mb-3" variant="destructive">
        <CircleAlert aria-hidden="true" />
        <AlertDescription className="flex w-full items-center justify-between gap-3 text-current">
          <span>{t("administration.situationCard.unappliedSources", { count: sources.length })}</span>
          <Button
            className="h-8! min-h-8! shrink-0 px-2.5! py-0! text-sm!"
            controlId="situation.sources.unapplied"
            onClick={() => setOpen(true)}
            type="button"
            variant="secondary"
          >
            {t("administration.situationCard.viewUnapplied")}
          </Button>
        </AlertDescription>
      </Alert>
      {open ? (
        <Dialog onOpenChange={() => undefined} open>
          <DialogContent
            className="sm:max-w-md"
            closeLabel={t("common.close")}
            onEscapeKeyDown={(event) => event.preventDefault()}
            onInteractOutside={(event) => event.preventDefault()}
            showCloseButton={false}
          >
            <DialogHeader>
              <DialogTitle>{t("administration.situationCard.unappliedTitle")}</DialogTitle>
              <DialogDescription>{t("administration.situationCard.unappliedRemoveDetail")}</DialogDescription>
            </DialogHeader>
            <ul className="divide-y divide-border">
              {sources.map((source) => (
                <li className="py-2.5" key={source.id}>
                  <SituationSourceSummary
                    excerpt={source.excerpt}
                    href={source.href}
                    id={source.id}
                    showHref={false}
                    tag={source.tag}
                    title={source.title}
                  />
                </li>
              ))}
            </ul>
            <DialogFooter>
              <Button
                className="border-transparent! bg-[var(--destructive-solid)]! text-inverse! shadow-xs! hover:bg-[var(--destructive-solid)]!"
                controlId="situation.sources.unapplied.acknowledge"
                disabled={pending}
                onClick={onAcknowledge}
                type="button"
              >
                {t("administration.situationCard.acknowledgeUnapplied")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}
